import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useCart } from "../context/CartContext";
import api from "../services/api";
import "../css/placeorder.css";

const PlaceOrder = () => {
  const { cartItems, cartTotal, clearCart, authReady } = useCart();

  const [addresses, setAddresses] = useState([]);
  const [defaultShipping, setDefaultShipping] = useState(null);
  const [defaultBilling, setDefaultBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [useSameAddress, setUseSameAddress] = useState(false);
  const [orderData, setOrderData] = useState({
    shipping_address_id: "",
    billing_address_id: "",
    payment_method: "cash",
    note: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (authReady && cartItems === null) {
      toast.error("Please login to access this page");
      navigate("/login");
    }
  }, [authReady, cartItems, navigate]);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const [shippingRes, billingRes, allRes] = await Promise.all([
          api.get("/addresses/default?type=shipping"),
          api.get("/addresses/default?type=billing"),
          api.get("/addresses"),
        ]);

        setAddresses(allRes.data.addresses || []);
        setDefaultShipping(shippingRes.data);
        setDefaultBilling(billingRes.data);

        setOrderData((prev) => ({
          ...prev,
          shipping_address_id: shippingRes.data?._id || "",
          billing_address_id: billingRes.data?._id || "",
        }));
      } catch (error) {
        toast.error("Failed to load addresses");
      } finally {
        setLoading(false);
      }
    };

    if (authReady) fetchAddresses();
  }, [authReady]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrderData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e) => {
    const checked = e.target.checked;
    setUseSameAddress(checked);
    setOrderData((prev) => ({
      ...prev,
      billing_address_id: checked ? prev.shipping_address_id : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const shippingId = orderData.shipping_address_id || defaultShipping?._id;
    const billingId =
      orderData.billing_address_id || defaultBilling?._id || shippingId;

    if (!shippingId || !billingId) {
      toast.error("No default or selected addresses found");
      setSubmitting(false);
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      setSubmitting(false);
      return;
    }

    try {
      const response = await api.post("/orders", {
        shipping_address_id: shippingId,
        billing_address_id: billingId,
        payment_method: orderData.payment_method,
        note: orderData.note,
      });
      toast.success("Order placed successfully!");
      await clearCart();
      navigate("/order-confirmation", {
        state: {
          orderId: response.data.order._id,
          orderNumber: response.data.order?.order_id,
        },
      });
    } catch (error) {
      console.error("Order placement failed:", error);
      toast.error(error.response?.data?.message || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading your addresses...</p>
      </div>
    );
  }

  return (
    <div className="place-order-container">
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="order-header">
        <h2>Place Your Order</h2>
        <p className="order-steps">
          Review your items and enter shipping details
        </p>
      </div>

      <div className="order-layout">
        <form onSubmit={handleSubmit} className="order-form">
          <div className="form-section">
            <h3>Shipping Address</h3>
            {defaultShipping && (
              <p className="default-preview">
                Default: {defaultShipping.street}, {defaultShipping.city},{" "}
                {defaultShipping.postal_code}
              </p>
            )}
            <select
              name="shipping_address_id"
              value={orderData.shipping_address_id}
              onChange={(e) => {
                handleChange(e);
                if (useSameAddress) {
                  setOrderData((prev) => ({
                    ...prev,
                    billing_address_id: e.target.value,
                  }));
                }
              }}
            >
              <option value="">Use Default or Select Address</option>
              {addresses.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.street}, {a.city}, {a.postal_code}
                </option>
              ))}
            </select>
          </div>

          <div className="form-section">
            <h3>Billing Address</h3>
            {defaultBilling && (
              <p className="default-preview">
                Default: {defaultBilling.street}, {defaultBilling.city},{" "}
                {defaultBilling.postal_code}
              </p>
            )}
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="sameAsShipping"
                checked={useSameAddress}
                onChange={handleCheckbox}
              />
              <label htmlFor="sameAsShipping">Same as shipping address</label>
            </div>

            {!useSameAddress && (
              <select
                name="billing_address_id"
                value={orderData.billing_address_id}
                onChange={handleChange}
              >
                <option value="">Use Default or Select Address</option>
                {addresses.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.street}, {a.city}, {a.postal_code}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-section">
            <h3>Payment Method</h3>
            <select
              name="payment_method"
              value={orderData.payment_method}
              onChange={handleChange}
              required
            >
              <option value="cash">Cash on Delivery</option>
              <option value="card" disabled>
                Credit Card (Coming Soon)
              </option>
              <option value="bank" disabled>
                Bank Transfer (Coming Soon)
              </option>
            </select>
          </div>

          <div className="form-section">
            <h3>Delivery Instructions</h3>
            <textarea
              name="note"
              value={orderData.note}
              onChange={handleChange}
              placeholder="Any special delivery instructions?"
              rows="3"
            />
          </div>

          <button
            type="submit"
            className="place-order-button"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner"></span> Processing...
              </>
            ) : (
              `Place Order (￦${cartTotal.toLocaleString()})`
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PlaceOrder;
